import styled, {css } from 'styled-components';

// Define a simple theme for consistent colors
const professionalTheme = {
  colors: {
    primary: '#4a90e2', // A softer, more professional blue
    primaryDark: '#357abd',
    primaryRgb: '74, 144, 226',
    secondary: '#8898aa',
    secondaryDark: '#6c7a89',
    background: '#f4f7f9', // A light, neutral background
    cardBackground: '#ffffff',
    text: '#3c4858', // A dark, readable text color
    border: '#e1e5eb',
    error: '#e74c3c', // A clear, but not too harsh, red
    success: '#2ecc71',
    warning: '#f39c12',
    lightGrey: '#f0f3f6',
  },
  shadows: {
    card: '0 2px 4px rgba(0, 0, 0, 0.08)',
    inputFocus: '0 0 0 3px rgba(74, 144, 226, 0.25)',
    buttonHover: '0 2px 6px rgba(0, 0, 0, 0.1)',
  },
};

export const BuyStallForm = styled.form`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  border-radius: 12px;
  background-color: ${professionalTheme.colors.background};
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  max-width: 100%;
  margin: 24px auto;
  border: 1px solid ${professionalTheme.colors.border};
  position: relative;
  transition: all 0.3s ease-in-out;
`;

export const FormContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-x: auto;
  padding-bottom: 20px;
`;

// New styles for horizontal alignment within each form group
export const FormGroup = styled.div<{error?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 15px;
  width: 100%; // Ensure form group takes full width
  flex: 1; // Allows form groups to grow and fill available space in FormRow

  // Revert to a vertical layout on smaller screens for better readability
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  // Styles for the error state
  ${(props) =>
    props.error &&
    css`
      input,
      select,
      textarea {
        border-color: ${professionalTheme.colors.error};
        &:focus {
          box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.25);
        }
      }
    `}
`;

export const Label = styled.label`
  font-weight: 600;
  color: ${professionalTheme.colors.text};
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95em;
  flex-shrink: 0; // Prevent the label from shrinking
  min-width: 150px; // Provide a consistent width for labels on desktop

  @media (max-width: 768px) {
    min-width: unset; // Remove fixed width on smaller screens
    width: 100%;
  }

  svg {
    color: ${professionalTheme.colors.primary};
    font-size: 1.1em;
  }
`;

export const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid ${professionalTheme.colors.border};
  border-radius: 8px;
  font-size: 1em;
  color: ${professionalTheme.colors.text};
  background-color: ${professionalTheme.colors.cardBackground};
  width: 100%;
  flex-grow: 1; // Allows the input to grow and fill the remaining space
  transition: all 0.2s ease-in-out;

  &:focus {
    border-color: ${professionalTheme.colors.primary};
    outline: none;
    box-shadow: ${professionalTheme.shadows.inputFocus};
  }

  &:disabled {
    background-color: ${professionalTheme.colors.lightGrey};
    cursor: not-allowed;
    color: ${professionalTheme.colors.secondary};
  }
`;

export const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid ${professionalTheme.colors.border};
  border-radius: 8px;
  font-size: 1em;
  color: ${professionalTheme.colors.text};
  background-color: ${professionalTheme.colors.cardBackground};
  cursor: pointer;
  width: 100%;
  flex-grow: 1; // Allows the select to grow and fill the remaining space
  transition: all 0.2s ease-in-out;

  &:focus {
    border-color: ${professionalTheme.colors.primary};
    outline: none;
    box-shadow: ${professionalTheme.shadows.inputFocus};
  }

  &:disabled {
    background-color: ${professionalTheme.colors.lightGrey};
    cursor: not-allowed;
    color: ${professionalTheme.colors.secondary};
  }
`;

export const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid ${professionalTheme.colors.border};
  border-radius: 8px;
  font-size: 1em;
  color: ${professionalTheme.colors.text};
  background-color: ${professionalTheme.colors.cardBackground};
  resize: vertical;
  width: 100%;
  flex-grow: 1; // Allows the textarea to grow and fill the remaining space
  transition: all 0.2s ease-in-out;

  &:focus {
    border-color: ${professionalTheme.colors.primary};
    outline: none;
    box-shadow: ${professionalTheme.shadows.inputFocus};
  }
`;

export const Button = styled.button`
  padding: 14px 24px;
  background-color: ${professionalTheme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1em;
  font-weight: 600;
  transition: all 0.3s ease;
  min-width: 150px;
  box-shadow: 0 4px 10px rgba(74, 144, 226, 0.2);

  &:hover {
    background-color: ${professionalTheme.colors.primaryDark};
    box-shadow: 0 6px 12px rgba(74, 144, 226, 0.3);
  }

  &:disabled {
    background-color: ${professionalTheme.colors.secondary};
    box-shadow: none;
    cursor: not-allowed;
  }
`;

export const FormSection = styled.div`
  padding: 24px;
  border: 1px solid ${professionalTheme.colors.border};
  border-radius: 12px;
  background-color: ${professionalTheme.colors.cardBackground};
  box-shadow: ${professionalTheme.shadows.card};
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  color: ${professionalTheme.colors.primary};
  font-size: 1.6em;
  font-weight: 700;
  border-bottom: 2px solid ${professionalTheme.colors.border};
  padding-bottom: 12px;
  line-height: 1;

  h3 {
    margin: 0;
    font-size: 1em;
  }

  svg {
    font-size: 1.2em;
  }
`;

export const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

export const FileUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  border: 2px dashed ${professionalTheme.colors.border};
  padding: 20px;
  border-radius: 12px;
  background-color: ${professionalTheme.colors.lightGrey};
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  flex-grow: 1; // Allows the file upload area to grow
  
  &:hover {
    border-color: ${professionalTheme.colors.primary};
    background-color: #f8fbfd;
  }
`;

export const FileUploadLabel = styled.label`
  background-color: ${professionalTheme.colors.secondary};
  color: white;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${professionalTheme.colors.secondaryDark};
  }
`;

export const FileInput = styled.input`
  display: none;
`;

export const PreviewImage = styled.img`
  max-width: 100px;
  max-height: 100px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid ${professionalTheme.colors.border};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const ImagePreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

export const MapContainer = styled.div`
  margin-top: 15px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid ${professionalTheme.colors.border};
`;

export const RequiredIndicator = styled.span`
  color: ${professionalTheme.colors.error};
  margin-left: 4px;
  font-size: 0.9em;
`;

export const ErrorMessage = styled.p`
  color: ${professionalTheme.colors.error};
  font-size: 0.85em;
  margin-top: 8px;
  margin-bottom: 0;
  font-weight: 500;
`;

export const RadioContainer = styled.div`
  display: flex;
  flex-direction: row; // Changed to row for horizontal radio buttons
  gap: 20px;
  margin-top: 5px;
  flex-grow: 1;
`;

export const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 1em;
  gap: 8px;
  cursor: pointer;
`;

export const RadioInput = styled.input`
  margin: 0;
  cursor: pointer;
`;
